import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'

import { RingCentralClient } from '../../lib/ringcentral/ringCentral'
import { updateAuthData } from '../../modules/Users/redux/userActions'

/**
 * Sends an SMS the way the native RingCentral Text app does: P2P from the user's
 * own RC number, using the user's OAuth token in the browser (same path as
 * click-to-call's makeCall). No A2P/10DLC, so no "[from] value is invalid".
 *
 * The RC call happens client-side; the CRM then logs the message (persist row +
 * TEXT_MESSAGE activity, ACL-checked) via POST /ringcentral-sms/send with the
 * returned rcMessageId, which tells the backend to skip its own A2P send.
 *
 * Returns the persisted row. Throws with a user-facing message on failure.
 */
const useSendSms = () => {
  const dispatch = useDispatch()
  const user = useSelector((state) => state.auth.user)

  return async ({ contactId, toNumber, text }) => {
    const from = `${user.phoneNumber || ''}`.replace(/\D/g, '')
    const to = `${toNumber || ''}`.replace(/\D/g, '')

    if (!from) {
      throw new Error('Enter your phone number in your profile before texting.')
    }
    if (!to) {
      throw new Error('Invalid recipient number.')
    }

    const session = await axios.get('/users/userRcSession')
    if (!session || !session.data || !session.data.accessToken) {
      throw new Error('RingCentral session not found — login to RingCentral from your profile to send texts.')
    }

    const ringCentral = new RingCentralClient(user.userId)
    const result = await ringCentral.sendSMS({
      from,
      to,
      text: text.trim(),
      user: {
        id: user.userId,
        accessToken: session.data.accessToken,
        expireTime: session.data.expireTime,
        expiresIn: session.data.expiresIn,
        refreshToken: session.data.refreshToken,
        refreshTokenExpireTime: session.data.refreshTokenExpireTime,
        refreshTokenExpiresIn: session.data.refreshTokenExpiresIn,
      },
    })

    if (result.error) {
      throw new Error(result.errorMessage || 'Failed to send SMS')
    }

    // Persist the rotated token so the next send/call reuses it (mirrors makeCall)
    if (result.refreshedAccessToken && Object.keys(result.refreshedAccessToken).length) {
      dispatch(updateAuthData(result.refreshedAccessToken, user.userId))
    }

    // Log to CRM (ACL-checked persist + activity); rcMessageId => backend skips A2P send
    const response = await axios.post('/ringcentral-sms/send', {
      contactId: contactId || null,
      toNumber,
      text: text.trim(),
      rcMessageId: result.smsResponse && result.smsResponse.id,
      rcMessageStatus: result.smsResponse && result.smsResponse.messageStatus,
      rcCreationTime: result.smsResponse && result.smsResponse.creationTime,
    })

    return response.data
  }
}

export default useSendSms
